import { Button, Col, DatePicker, Form, Input, Row } from "antd"
import { useTranslation } from "react-i18next"
import { CustomSelect, PhoneInput } from "ui/Input"
import LabeledField from "./LabeledField"
import { genderOptions } from "utils/gender"
import ProfileActionButtons from "./ActionButtons"
import { CountrySelector } from "ui/Input/CountrySelector"
import { AddAvatar } from "ui/PersonalInfo/ProfileInformation/AddAvatar"
import { useSanitizedTextInput } from "hooks/UseSanitizedTextInput"
import { useCallback, useMemo, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import dayjs from "dayjs"
import UpdateMobileNumber from "../../Modals/UpdateMobileNumber"
import { sendOtpToVerifyPhone, updateUser, verifyOtpForPhoneNumberUpdation } from "store/user"
import { toaster } from "components/common/Toast"
import useSendOTP from "../../Hooks/useSendOTP"
import LinkedWithAccount from "./LinkedWithAccount"

const ProfileInfo = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [avatarUrl, setAvatarUrl] = useState("")
  const { first_name, avatar, gender, last_name, email, phone_number, birthday, street_address, state, country, city, zip_code } =
    useSelector((state) => state.user)
  const dispatch = useDispatch()
  const [isSending, setIsSending] = useState(false)
  const { cooldown, sendCodeLabel, hasSentCode, markSent, resetOTP } = useSendOTP()

  const [isOpenUpdateMobileNumberModal, setIsOpenUpdateMobileNumberModal] = useState(false)
  const [nextPhone, setNextPhone] = useState({ fullNumber: "", countryCode: "" })
  const [phoneResetKey, setPhoneResetKey] = useState(0)

  const handleImageChange = async (e) => {
    form.setFields([{ name: "avatar", errors: [] }])
    const file = e.target?.files?.[0]
    if (file) {
      onUpload(file)
    }
  }

  const onUpload = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const base64 = e.target.result
      form.setFieldsValue({ avatar: file })
      setAvatarUrl(base64)
    }
    reader.readAsDataURL(file)
  }

  const handleSendCode = useCallback(async () => {
    if (cooldown > 0 || isSending) return
    setIsSending(true)
    try {
      const payload={
        phone_number: nextPhone?.fullNumber,
        country_code: nextPhone?.countryCode
      }
      const response = await dispatch(sendOtpToVerifyPhone(payload))
      toaster.success(response?.message)
      markSent(60)
    } finally {
      setIsSending(false)
    }
  }, [cooldown, isSending, dispatch, nextPhone, markSent])

  const initialValues = useMemo(
    () => ({
      first_name,
      last_name,
      email,
      phone_number,
      street_address,
      state,
      country,
      city,
      zip_code,
      gender,
      birthday: birthday ? dayjs(birthday) : null
    }),
    [first_name, last_name, email, phone_number, street_address, state, country, city, zip_code, gender, birthday]
  )

  const handleConfirmCode = useCallback(async () => {
    const code = form.getFieldValue("input_code")
    if (!code) return
    setIsSending(true)
    try {
      const payload={
        phone_number: nextPhone?.fullNumber,
        country_code: nextPhone?.countryCode,
        otp:code,
      }
      const response = await dispatch(verifyOtpForPhoneNumberUpdation(payload))
      toaster.success(response?.message)
      setIsOpenUpdateMobileNumberModal(false)
      resetOTP()
      form.setFieldsValue({ input_code: undefined })
    } finally {
      setIsSending(false)
    }
  }, [dispatch, form, nextPhone, resetOTP])

  const handleMobileUpdateReset = useCallback(() => {
    resetOTP()
    setIsSending(false)
    form.setFieldsValue({ input_code: undefined, update_phone_number: "" })
    setNextPhone("")
    setPhoneResetKey((k) => k + 1)
  }, [form, resetOTP])

  const firstNameHandlers = useSanitizedTextInput({ form, field: "first_name", variant: "singleWordNoDigits" })
  const lastNameHandlers = useSanitizedTextInput({ form, field: "last_name", variant: "lettersSpaces" })
  const cityHandlers = useSanitizedTextInput({ form, field: "city", variant: "lettersSpaces" })
  const stateHandlers = useSanitizedTextInput({ form, field: "state", variant: "lettersSpaces" })

  const handleUpdate = useCallback(
    async (values) => {
      try {
        const { email, disabled_phone_number, update_phone_number, ...rest } = values || {}

        const payload = { ...rest }
        if (payload?.birthday) {
          payload.birthday = payload.birthday.format("YYYY-MM-DD")
        }

        const formData = new FormData()
        for (const [key, val] of Object.entries(payload)) {
          if (val === undefined || val === null) continue
          if (key === "avatar" && val instanceof File) {
            formData.append("avatar", val)
          } else if (key === "street_address") {
            formData.append("address_line_1", val)
          } else {
            formData.append(key, val)
          }
        }
        const response = await dispatch(updateUser(formData))
        toaster.success(response?.message)
      } catch (error) {
        console.error(error)
      }
    },
    [dispatch]
  )

  const handleProfileCancel = () => {
    form.resetFields()
    setAvatarUrl("")
  }

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleUpdate}>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <LabeledField labelKey="client.profile.tabs.profile.profile_photo" name="avatar">
            <AddAvatar
              key={avatarUrl ? "preview" : "initial"}
              size={100}
              avatar={avatarUrl || avatar}
              handleImageChange={handleImageChange}
            />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField
            labelKey="client.profile.tabs.profile.first_name"
            name="first_name"
            rules={[{ required: true, message: t("client.profile.tabs.profile.first_name_is_required") }]}
          >
            <Input {...firstNameHandlers} autoComplete="given-name" />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField
            labelKey="client.profile.tabs.profile.last_name"
            name="last_name"
            rules={[{ required: true, message: t("client.profile.tabs.profile.last_name_is_required") }]}
          >
            <Input {...lastNameHandlers} autoComplete="family-name" />
          </LabeledField>
        </Col>

        <Col xs={24} sm={12}>
          <LabeledField
            labelKey="client.profile.tabs.profile.email"
            name="email"
            rules={[{ required: true, message: t("client.profile.tabs.profile.email_is_required") }]}
          >
            <Input disabled />
          </LabeledField>
          <LinkedWithAccount />
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField labelKey="client.profile.tabs.profile.phone_number" name="disabled_phone_number">
            <PhoneInput initialValue={phone_number} disabled />
            <Button className="w-full justify-end" type="link" onClick={() => setIsOpenUpdateMobileNumberModal(true)}>
              {t("client.profile.tabs.profile.update_phone_number")}
            </Button>
          </LabeledField>
        </Col>

        <Col xs={24} sm={12}>
          <LabeledField labelKey="client.profile.tabs.profile.gender" name="gender">
            <CustomSelect options={genderOptions} placeholder={t("client.profile.tabs.profile.gender")} />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField
            labelKey="client.profile.tabs.profile.dob"
            name="birthday"
            rules={[{ required: true, message: t("client.profile.tabs.profile.dob_is_required") }]}
          >
            <DatePicker
              format="MM/DD/YYYY"
              className="w-full"
              style={{ height: "42px" }}
              disabledDate={(current) => current && current > dayjs().endOf("day")}
            />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField labelKey="client.profile.tabs.profile.street_address" name="street_address">
            <Input />
          </LabeledField>
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12}>
          <LabeledField labelKey="client.profile.tabs.profile.city" name="city">
            <Input {...cityHandlers} />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField labelKey="client.profile.tabs.profile.state" name="state">
            <Input {...stateHandlers} />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField
            labelKey="client.profile.tabs.profile.zip"
            name="zip_code"
            rules={[
              {
                pattern: /^[A-Za-z0-9][A-Za-z0-9\s-]{0,10}[A-Za-z0-9]$/,
                message: t("listings.tabs.location.validation.invalid_zip_code")
              }
            ]}
          >
            <Input />
          </LabeledField>
        </Col>
        <Col xs={24} sm={12}>
          <LabeledField labelKey="client.profile.tabs.profile.country" name="country">
            <CountrySelector />
          </LabeledField>
        </Col>
      </Row>
      <ProfileActionButtons handleProfileCancel={handleProfileCancel} />

      {isOpenUpdateMobileNumberModal && (
        <UpdateMobileNumber
          open={isOpenUpdateMobileNumberModal}
          setOpen={setIsOpenUpdateMobileNumberModal}
          onClose={() => {
            handleMobileUpdateReset()
            setIsOpenUpdateMobileNumberModal(false)
          }}
          hasSentCode={hasSentCode}
          cooldown={cooldown}
          isSending={isSending}
          sendCodeLabel={sendCodeLabel}
          handleSendCode={handleSendCode}
          handleConfirmCode={handleConfirmCode}
          handleUpdateReset={handleMobileUpdateReset}
          resetKey={phoneResetKey}
          phone_number={phone_number}
          phoneValue={nextPhone}
          onPhoneChange={setNextPhone}
        />
      )}
    </Form>
  )
}

export default ProfileInfo
